import { useCallback, useEffect, useMemo, useState } from "react";
import type { HaryanaGnaTgna } from "../../../models/rajasthan";
import { getHaryanaGnaTgnaData } from "../../../Rest_api/RestApi";
import FilterMenu from "../../../components/FilterMenu";
import Loading from "../../../components/Loading";
import { FUNCTIONS } from "../const_hppc";
import { R_CHART_TYPES } from "../../Rajasthan/Utility/const";
import {
  ChartDataParams,
  FormatChartData,
} from "../../../components/recharts/functions";
import { ReMixChart } from "../../../components/recharts/ReMixCharts";
import { convertToString, getMonthFromDate } from "../../../extensions/date";
import Tooltip from "../../../components/tooltip";
import MainPageContainer from "../../../components/MainPageContainer";
import NewFilterContainer from "../../../components/NewFilterContainer ";
import { useSelector } from "react-redux";
import SecondaryHeader from "../../Rajasthan/Demand/SecondaryHeader";
import LegendSummaryTable, {
  ChartTable,
  downloadChartTableCSV,
  downloadSummaryCSV,
} from "../../../components/table/SummaryTable";
import { SearchBox } from "../../../components/Search";
import { BrushStart, LegendKey } from "../../../models/chart_model";
import { renderHourTick } from "../../../components/recharts/components";
import { stringToConsistentColor } from "../../../extensions/string";
import { AddDatesWithFileName } from "../../../helpers/helper";

const GROUP_BY = [
  {
    label: "State",
  },
  {
    label: "Utility",
  },
  {
    label: "IR Link",
  },
  {
    label: "Trader",
  },
  // {
  //   label: "Generator",
  // },
];

const KEYS = [
  {
    label: "Time Slot",
    key: "time",
    name: "Time Slot",
    firstKey: "slot",
    secondKey: "date",
    values: ["slot", "date"],
    isTime: true,
  },
  {
    label: "Date",
    key: "date",
    name: "Date",
    firstKey: "date",
    secondKey: undefined,
    values: ["date"],
    isTime: false,
  },
  {
    label: "Month",
    key: "month",
    name: "Month",
    firstKey: "month",
    secondKey: undefined,
    values: ["month"],
    isTime: false,
  },
];

const UNITS = ["MW", "MWh"];

const PERIPHERY = [
  {
    label: "Generator",
    value: "Generator Periphery"
  },
  {
    label: "State",
    value: "State Periphery"
  }
];

// eslint-disable-next-line @typescript-eslint/no-redeclare
export default function HaryanaGnaTgna() {

  const [TableData, setTableData] = useState<any[]>([]);
  const dataTab = useSelector((state: any) => state.viewMode.activeView);
  const activeTab = useSelector((state: any) => state.switchTabs.activeTab);
  const DefaultDates = useSelector(
    (state: any) => state.defaultDatesSetter.defaultDates
  );
  const [uniqueBlock, setUniqueBlock] = useState<any[]>([]);
  const [data, setData] = useState<HaryanaGnaTgna[]>([]);
  const [selectedBlock, setSelectedBlock] = useState<any[]>([]);
  const [selectedKey, setSelectedKey] = useState(KEYS[0]);
  const [chartKeys, setChartKeys] = useState<any[]>([]);
  const [chartData, setChartData] = useState<any[]>([]);
  const [selectedGroupKeys, setSelectedGroupKeys] = useState(GROUP_BY[0]);
  const [selectedChartType, setSelectedChartType] = useState(R_CHART_TYPES[2]);
  const [loading, setLoading] = useState(true);
  const [selectedVariable, setSelectedVariable] = useState<"Buy" | "Sell">(
    "Buy"
  );
  const [selectedCategory, setSelectedCategory] = useState<
    "OA GNA" | "OA TGNA" | "Total"
  >("OA GNA");
  const [selectedFunction, setSelectedFunction] = useState(FUNCTIONS[0]);
  const [selectedPeriphery, setSelectedPeriphery] = useState(PERIPHERY[0]);
  const [selectedUnit, setSelectedUnit] = useState(UNITS[0]);
  // Track which legends are selected in the chart (empty = all selected)
  const [activeLegends, setActiveLegends] = useState<LegendKey[]>([]);

  const _handlefetchData = useCallback(async () => {
    if (DefaultDates[activeTab].defaultRangeDate === undefined) {
      return;
    }
    setLoading(true);
    const dates = DefaultDates[activeTab].defaultRangeDate;

    const response = await getHaryanaGnaTgnaData({
      startDate: convertToString(dates.startDate),
      endDate: convertToString(dates.endDate),
    });

    const processedGnaTgnaData = response.map((item) => {
      const isBuy = item.type__name === "Drawal";
      return {
        ...item,
        type: `${item.category__category} ${isBuy ? "Buy" : "Sell"}`,
        buy_sell: isBuy ? "Buy" : "Sell",
        value_mwh: Math.abs(item.value_mw) / 4,
        value_mw: Math.abs(item.value_mw),
        month: getMonthFromDate(item.date),
        generator_source: item.generator_source
          ? item.generator_source.charAt(0).toUpperCase() +
          item.generator_source.slice(1)
          : item.generator_source,
      };
    });

    setData(processedGnaTgnaData);
    setUniqueBlock(
      Array.from(new Set(processedGnaTgnaData.map((item) => item.slot)))
    );

    // Debug: Check what fields are available in the first item
    if (processedGnaTgnaData.length > 0) {
      console.log("Available fields:", Object.keys(processedGnaTgnaData[0]));
      console.log("First item data:", processedGnaTgnaData[0]);
    }
    console.log(processedGnaTgnaData);
    setLoading(false);
  }, [DefaultDates, activeTab]);

  const getKeys = useCallback(() => {
    if (selectedKey.key === "time") {
      return ["date", "slot", "slot_with_time"];
    } else if (selectedKey.key === "date") {
      return ["date"];
    } else {
      return [selectedKey.values[0]];
    }
  }, [selectedKey]);

  useEffect(() => {
    let filteredData = data;

    // Block filter
    if (selectedBlock.length > 0) {
      filteredData = filteredData.filter((item) =>
        selectedBlock.map((item) => item.value).includes(item.slot)
      );
    }

    // Variable filter (Buy/Sell)
    if (selectedVariable === "Buy") {
      filteredData = filteredData.filter((item) => item.buy_sell === "Buy");
    } else {
      filteredData = filteredData.filter((item) => item.buy_sell === "Sell");
    }

    // Category filter
    if (selectedCategory !== "Total") {
      filteredData = filteredData.filter(
        (item) => item.category__category === selectedCategory.replace(" ", "_")
      );
    }

    // Periphery filter
    filteredData = filteredData.filter(
      (item) => item.boundary_type__boundary_type === selectedPeriphery.value
    );

    function getGroupByKey() {
      if (selectedGroupKeys.label === "State") {
        if (selectedVariable === "Buy") {
          return ["from_state__name", "to_state__name"];
        } else {
          return ["from_state__name", "to_state__name"];
        }
      }

      if (selectedGroupKeys.label === "Utility") {
        if (selectedVariable === "Buy") {
          return ["from_utility__name", "to_utility__name"];
        } else {
          return ["to_utility__name", "from_utility__name"];
        }
      }

      if (selectedGroupKeys.label === "IR Link") {
        if (selectedVariable === "Buy") {
          return ["ir_link__name"];
        } else {
          return ["ir_link__name"];
        }
      }

      if (selectedGroupKeys.label === "Trader") {
        if (selectedVariable === "Buy") {
          return ["trader__name"];
        } else {
          return ["trader__name"];
        }
      }

      if (selectedGroupKeys.label === "Category") {
        if (selectedVariable === "Buy") {
          return ["category__category"];
        } else {
          return ["category__category"];
        }
      }

      if (selectedGroupKeys.label === "Generator") {
        if (selectedVariable === "Buy") {
          return ["generator_source"];
        } else {
          return ["generator_source"];
        }
      }

      return [];
    }

    const groupByKeys = selectedFunction.comparative ? getGroupByKey() : [];
    if (selectedCategory !== "Total") {
      groupByKeys.push("category__category");
    }
    groupByKeys.push("buy_sell");

    // Create data with slot_with_time for table display
    const dataWithSlotTime = filteredData.map(item => ({
      ...item,
      slot_with_time: item.slot && item.time_string ? `${item.slot} (${item.time_string})` : item.slot || "-"
    }));

    const par: ChartDataParams = {
      keys: getKeys(),
      tableKeys: ["date", "slot", "slot_with_time"],
      groupBy: groupByKeys,
      volumeKey: "",
      aggregationKeys: ["value_mwh", "value_mw"],
      averageKeys: ["value_mw"],
      averageDuplicationKeys: ["slot", "date", "slot_with_time"],
      priceKey: "",
      data: dataWithSlotTime,
    };

    const cData = FormatChartData(par);

    // Ensure slot_with_time is properly set in table data
    const processedTableData = (cData.tableData || []).map(row => ({
      ...row,
      slot_with_time: row.slot && row.time_string
        ? `${row.slot} (${row.time_string})`
        : row.slot_with_time || row.slot || "-"
    }));

    setTableData(processedTableData);
    setChartData(cData.data);
    setChartKeys(cData.keys);
  }, [
    data,
    selectedGroupKeys,
    selectedFunction,
    selectedPeriphery,
    getKeys,
    selectedBlock,
    selectedVariable,
    selectedCategory,
  ]);

  useEffect(() => {
    _handlefetchData();
  }, [_handlefetchData]);

  const handlexDataKey = useCallback(() => {
    if (selectedKey.key === "time") {
      if (selectedBlock.length > 0) {
        return "slot";
      }
      return "slot";
    } else {
      return selectedKey.key;
    }
  }, [selectedBlock.length, selectedKey.key]);

  const xDataKey = useMemo(() => handlexDataKey(), [handlexDataKey]);

  const summaryExtraKeys = selectedKey.isTime
    ? [
      "slot_with_time",

      selectedKey.values[1],
    ]
    : [selectedKey.values[0]];

  const summaryExtraLabels = selectedKey.isTime
    ? ["Time Slot", selectedKey.values[1] === "date" ? "Date" : selectedKey.values[1]]
    : [selectedKey.values[0] === "date" ? "Date" : selectedKey.values[0]];

  function getActiveLegends() {
    return chartKeys.map((key, index) => {
      let name = key;

      if (
        selectedGroupKeys.label === "State" ||
        selectedGroupKeys.label === "Utility"
      ) {
        name = name
          .replace("_", " -> ")
          .replaceAll("_", " ")
          .replace("Buy", " (Buy)")
          .replace("Sell", " (Sell)");
      } else {
        name = name
          .replaceAll("_", " ")
          .replaceAll("null", "Others")
          .replaceAll("undefined", " ")
          .replace("Buy", " (Buy)")
          .replace("Sell", " (Sell)");
      }

      return {
        dataKey: key + `_value_${selectedUnit.toLowerCase()}`,
        name: name,
        legendColor: undefined,
        stroke: stringToConsistentColor(key),
        type: selectedChartType.value,
        stackId: selectedChartType.stackId !== undefined
          ? (key.includes("Buy") ? "buy" : "sell")
          : undefined,
      };
    });
  }

  // For summary/table: use only the selected (active) legends, or all if none selected
  const allLegendsGna = getActiveLegends();
  const visibleLegendsGna =
    activeLegends.length > 0
      ? allLegendsGna.filter((l) =>
          activeLegends.some((al: LegendKey) => al.dataKey === l.dataKey)
        )
      : allLegendsGna;

  const tablePar = {
    legends: visibleLegendsGna,
    data: chartData || [],
    tableData: TableData,
    itemsPerPage: 96,
    unit: selectedUnit,
    extraKeys: summaryExtraKeys,
    extraLabels: summaryExtraLabels,
    filename: AddDatesWithFileName({
      fileName: "Power Procurement Gna/Tgna",
      dates: DefaultDates[activeTab].defaultRangeDate,
    }),
  };

  function secondDataKey() {
    if (selectedKey.isTime) {
      if (selectedBlock.length > 0) {
        return "date";
      }
      return "date";
    } else {
      return undefined;
    }
  }

  const handleKeyChange = (index: number) => {
    setSelectedKey(KEYS[index]);
  };

  // Check if there's no data to display
  const hasNoData = !loading && (chartData.length === 0 || chartKeys.length === 0);

  return (
    <div className="gas-page-container">
      {loading && <Loading />}
      <NewFilterContainer>
        <div className="side-filters-container text-nowrap">
          <FilterMenu
            items={KEYS.map((item) => item.name)}
            selectedItems={[selectedKey.name]}
            onSelect={(item, index) => {
              handleKeyChange(index);
            }}
            title={"Interval"}
          />

          <FilterMenu
            items={UNITS}
            selectedItems={[selectedUnit]}
            onSelect={(item, index) => {
              setSelectedUnit(item);
            }}
            title={"Unit"}
          />

          <FilterMenu
            title="Function"
            items={FUNCTIONS.map((item) => item.label)}
            selectedItems={[selectedFunction.label]}
            onSelect={(item, index) => {
              setSelectedFunction(FUNCTIONS[index]);
            }}
          />

          <FilterMenu
            title="Periphery"
            items={PERIPHERY.map((item) => item.label)}
            selectedItems={[selectedPeriphery.label]}
            onSelect={(item, index) => {
              setSelectedPeriphery(PERIPHERY[index]);
            }}
          />

          <FilterMenu
            title="Variable"
            items={["Buy", "Sell"]}
            selectedItems={[selectedVariable]}
            onSelect={(item, index) => {
              setSelectedVariable(item as "Buy" | "Sell");
            }}
          />

          <FilterMenu
            title="Category"
            items={["Total", "OA GNA", "OA TGNA"]}
            selectedItems={[selectedCategory]}
            onSelect={(item, index) => {
              setSelectedCategory(item as "OA GNA" | "OA TGNA" | "Total");
            }}
          />

          <FilterMenu
            title="Group By"
            items={GROUP_BY.map((item) => item.label)}
            selectedItems={[selectedGroupKeys.label]}
            onSelect={(item, index) => {
              setSelectedGroupKeys(GROUP_BY[index]);
            }}
          />

          <FilterMenu
            title="Chart Type"
            items={R_CHART_TYPES.map((item) => item.label)}
            selectedItems={[selectedChartType.label]}
            onSelect={(item, index) => {
              setSelectedChartType(R_CHART_TYPES[index]);
            }}
          />
        </div>
      </NewFilterContainer>

      <MainPageContainer>
        <SecondaryHeader />
        <div className="gas-chart-container">
          <div className="gas-heading gap-x-10">
            <h2 className="chartHeading w-60">Volume Drawal (MW)</h2>
            <div className="flex gap-x-6  justify-end">
              <SearchBox
                options={uniqueBlock.map((item) => {
                  return { value: item, label: item };
                })}
                isMany={true}
                placeholder="Search Block"
                selectedOptions={selectedBlock}
                onChange={setSelectedBlock}
              />
              <button
                onClick={() => {
                  const secureTablePar = {
                    ...tablePar,
                    data: chartData || [],
                  };
                  dataTab.label === "Chart" &&
                    downloadChartTableCSV(secureTablePar);
                  dataTab.label === "Summary" &&
                    downloadSummaryCSV(secureTablePar);
                  dataTab.label === "Table" &&
                    downloadChartTableCSV(secureTablePar);
                }}
                className="downloadButton"
              >
                Download Data
              </button>
            </div>
          </div>

          {hasNoData ? (
            <div style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: '100%',
              minHeight: '400px',
              fontSize: '18px',
              color: '#666',
              fontWeight: '500'
            }}>
              No transactions found for the selected date and filter
            </div>
          ) : (
            <>
              {dataTab.label === "Chart" && (
                <ReMixChart
                  data={chartData}
                  xDataKey={xDataKey}
                  secondXDataKey={secondDataKey()}
                  unit={selectedUnit}
                  yAxisLabel={selectedUnit}
                  isTimeSlot={selectedKey.isTime}
                  showBrush={chartData.length > 96 ? true : undefined}
                  brushStart={BrushStart.Start}
                  onlyTitle={true}
                  brushIndex={{
                    startIndex: 0,
                    endIndex: 96 * 1,
                  }}
                  legends={getActiveLegends()}
                  xTick={
                    selectedKey.isTime && selectedBlock.length === 0
                      ? (value: any, index: any) => {
                        return renderHourTick(value, 10);
                      }
                      : undefined
                  }
                  defaultSelectedLegends={activeLegends}
                  onLegendSelect={(selected: LegendKey[]) => setActiveLegends(selected)}
                />
              )}
              {dataTab.label === "Summary" && <LegendSummaryTable {...tablePar} />}
              {dataTab.label === "Table" && <ChartTable {...tablePar} />}
            </>
          )}
        </div>
        <div className="gna-footer-container">
          <div className="grid grid-cols-3 justify-between gap-5 ">
            <Tooltip
              note={<div>WBES</div>}
              text="
                1. WBES: OA GNA reports, OA TGNA reports
                2. MW: Represents the average (MW) when the selected time interval is Date. "
            >
              <p className="gna-footer-text">Source</p>
            </Tooltip>
            <p className="gna-footer-text col-span-1 text-center">
              Powered by <strong>GNA Energy</strong>
            </p>
            <strong className="gna-footer-text col-span-1 text-end">
              Beta Version
            </strong>
          </div>
        </div>
      </MainPageContainer>
    </div>
  );
}